import React from 'react';
import { captureException } from '~/lib/posthog';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Application error:', error, errorInfo);
    captureException(error, { componentStack: errorInfo.componentStack });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            padding: '2rem',
            textAlign: 'center',
            fontFamily: '"DM Sans", system-ui, -apple-system, sans-serif',
            backgroundColor: '#0A0A0B',
            color: '#F4F4F5',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Atmospheric background orbs */}
          <div
            style={{
              position: 'fixed',
              inset: 0,
              pointerEvents: 'none',
              zIndex: 0,
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '20%',
                left: '30%',
                width: '400px',
                height: '400px',
                borderRadius: '50%',
                background: 'rgba(217, 242, 106, 0.06)',
                filter: 'blur(100px)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: '10%',
                right: '20%',
                width: '350px',
                height: '350px',
                borderRadius: '50%',
                background: 'rgba(217, 242, 106, 0.04)',
                filter: 'blur(120px)',
              }}
            />
          </div>

          {/* Content */}
          <div style={{ position: 'relative', zIndex: 1, maxWidth: '480px' }}>
            {/* Icon */}
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '16px',
                background: 'rgba(248, 113, 113, 0.1)',
                border: '1px solid rgba(248, 113, 113, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem',
                fontSize: '28px',
              }}
            >
              !
            </div>

            <h1
              style={{
                fontSize: '24px',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
                marginBottom: '0.75rem',
                color: '#F4F4F5',
              }}
            >
              Something went wrong
            </h1>

            <p
              style={{
                color: '#A1A1AA',
                fontSize: '15px',
                lineHeight: 1.6,
                marginBottom: '2rem',
              }}
            >
              An unexpected error occurred. Try reloading the page or heading back to the home page.
            </p>

            {this.state.error && (
              <details
                style={{
                  marginBottom: '2rem',
                  textAlign: 'left',
                }}
              >
                <summary
                  style={{
                    cursor: 'pointer',
                    color: '#71717A',
                    fontSize: '13px',
                    fontWeight: 500,
                    letterSpacing: '0.01em',
                    marginBottom: '0.5rem',
                  }}
                >
                  Error details
                </summary>
                <pre
                  style={{
                    padding: '1rem',
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    borderRadius: '12px',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: '180px',
                    color: '#A1A1AA',
                    fontSize: '13px',
                    fontFamily: '"JetBrains Mono", monospace',
                    lineHeight: 1.7,
                  }}
                >
                  {this.state.error.message}
                  {this.state.error.stack && `\n\n${this.state.error.stack}`}
                </pre>
              </details>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button
                onClick={this.handleReload}
                style={{
                  padding: '0.625rem 1.5rem',
                  borderRadius: '10px',
                  border: 'none',
                  background: '#D9F26A',
                  color: '#0A0A0A',
                  fontSize: '14px',
                  fontWeight: 600,
                  fontFamily: '"DM Sans", system-ui, sans-serif',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
                onMouseOver={(e) => (e.currentTarget.style.background = '#E6FF7A')}
                onMouseOut={(e) => (e.currentTarget.style.background = '#D9F26A')}
              >
                Reload Page
              </button>
              <button
                onClick={this.handleGoHome}
                style={{
                  padding: '0.625rem 1.5rem',
                  borderRadius: '10px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  background: 'rgba(255, 255, 255, 0.03)',
                  color: '#D4D4D8',
                  fontSize: '14px',
                  fontWeight: 500,
                  fontFamily: '"DM Sans", system-ui, sans-serif',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s, background 0.2s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                }}
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
